import { handleActions, Action } from 'redux-actions'
import { GetOrdersResponseData, Order, OrdersPagination } from 'types/order'
import { LoadingStatus } from 'types/loading'

import * as actions from './orders.actions'
import { isSilent } from './orders.utils'

export interface OrdersState {
  orders: Order[]
  loadingStatus: LoadingStatus
  loadingTakeOrderStatus: LoadingStatus
  loadingTakeNextOrderStatus: LoadingStatus
  takeOrderId: string | ''
  withoutExecutor: number | null
  numberOfOrdersPerDay: number | null
  orderFilterId: string
  orderFilterByExpert: string
  search: string
  pagination: OrdersPagination | null
  page: number
  pageSize: number
  finishedDateFrom: string
  finishedDateTo: string
}

export const initialOrdersState: OrdersState = {
  orders: [],
  loadingStatus: LoadingStatus.NONE,
  loadingTakeOrderStatus: LoadingStatus.NONE,
  loadingTakeNextOrderStatus: LoadingStatus.NONE,
  takeOrderId: '',
  withoutExecutor: null,
  numberOfOrdersPerDay: null,
  orderFilterId: '1',
  orderFilterByExpert: '',
  search: '',
  pagination: null,
  page: 1,
  pageSize: 20,
  finishedDateFrom: '',
  finishedDateTo: '',
}

const fetchOrdersRequest = (state: OrdersState, action: Action<string | void | actions.FetchOrdersRequestPayload>): OrdersState => ({
  ...state,
  ...(isSilent(action.payload) ? {} : { loadingStatus: LoadingStatus.PENDING }),
  search: typeof action.payload === 'string' ? action.payload : '',
})

const fetchOrdersPreload = (state: OrdersState): OrdersState => ({
  ...state,
  loadingStatus: LoadingStatus.SUCCESS,
})

const fetchOrdersSuccess = (state: OrdersState, { payload }: Action<GetOrdersResponseData>): OrdersState => ({
  ...state,
  orders: payload!.ordersList,
  withoutExecutor: payload!.withoutExecutor,
  numberOfOrdersPerDay: payload!.numberOfOrdersPerDay,
  pagination: payload!.pagination || null,
  page: payload!.pagination?.page ?? state.page,
  pageSize: payload!.pagination?.pageSize ?? state.pageSize,
  loadingStatus: LoadingStatus.SUCCESS,
})

const fetchOrdersFailure = (state: OrdersState): OrdersState => ({
  ...state,
  loadingStatus: LoadingStatus.FAILED,
})

const ordersChangeFilter = (state: OrdersState, action: Action<string>): OrdersState => ({
  ...state,
  orderFilterId: action.payload,
  page: 1,
  pagination: null,
})

const changeFilterByExpert = (state: OrdersState, action: Action<string>): OrdersState => ({
  ...state,
  orderFilterByExpert: action.payload,
})

const changeOrdersPage = (state: OrdersState, action: Action<number>): OrdersState => ({
  ...state,
  page: action.payload || 1,
})

const changeOrdersPageSize = (state: OrdersState, action: Action<number>): OrdersState => {
  const pageSize = action.payload || 20

  if (state.pageSize === pageSize) {
    return state.page === 1 ? state : { ...state, page: 1 }
  }

  return {
    ...state,
    pageSize,
    page: 1,
    pagination: null,
  }
}

const resetOrdersPage = (state: OrdersState): OrdersState => ({
  ...state,
  page: 1,
  pagination: null,
})

const changeFinishedOrdersPeriod = (state: OrdersState, action: Action<{ dateFrom: string; dateTo: string }>): OrdersState => ({
  ...state,
  finishedDateFrom: action.payload?.dateFrom || '',
  finishedDateTo: action.payload?.dateTo || '',
  page: 1,
  pagination: null,
})

const clearFinishedOrdersPeriod = (state: OrdersState): OrdersState => ({
  ...state,
  finishedDateFrom: '',
  finishedDateTo: '',
})

const setInitialState = (): OrdersState => ({
  ...initialOrdersState,
})

const fetchTakeOrderRequest = (state: OrdersState): OrdersState => ({
  ...state,
  loadingTakeOrderStatus: LoadingStatus.PENDING,
})

const fetchTakeOrderSuccess = (state: OrdersState): OrdersState => ({
  ...state,
  loadingTakeOrderStatus: LoadingStatus.SUCCESS,
})

const fetchTakeOrderFailure = (state: OrdersState): OrdersState => ({
  ...state,
  loadingTakeOrderStatus: LoadingStatus.FAILED,
})

const fetchTakeNextOrderRequest = (state: OrdersState): OrdersState => ({
  ...state,
  loadingTakeNextOrderStatus: LoadingStatus.PENDING,
  takeOrderId: '',
})

const fetchTakeNextOrderSuccess = (state: OrdersState, action: Action<string>): OrdersState => ({
  ...state,
  loadingTakeNextOrderStatus: LoadingStatus.SUCCESS,
  takeOrderId: action.payload,
})

const fetchTakeNextOrderFailure = (state: OrdersState): OrdersState => ({
  ...state,
  loadingTakeNextOrderStatus: LoadingStatus.FAILED,
  takeOrderId: '',
})

export default handleActions<OrdersState, any>(
  {
    [actions.FETCH_ORDERS_REQUEST]: fetchOrdersRequest,
    [actions.FETCH_ORDERS_PRELOADED]: fetchOrdersPreload,
    [actions.FETCH_ORDERS_SUCCESS]: fetchOrdersSuccess,
    [actions.FETCH_ORDERS_FAILURE]: fetchOrdersFailure,
    [actions.SEARCH_ORDERS_REQUEST]: fetchOrdersRequest,
    [actions.SEARCH_ORDERS_SUCCESS]: fetchOrdersSuccess,
    [actions.SEARCH_ORDERS_FAILURE]: fetchOrdersFailure,
    [actions.ORDERS_CHANGE_FILTER]: ordersChangeFilter,
    [actions.CHANGE_ORDER_FILTER_BY_EXPERT]: changeFilterByExpert,
    [actions.CHANGE_ORDERS_PAGE]: changeOrdersPage,
    [actions.CHANGE_ORDERS_PAGE_SIZE]: changeOrdersPageSize,
    [actions.RESET_ORDERS_PAGE]: resetOrdersPage,
    [actions.CHANGE_FINISHED_ORDERS_PERIOD]: changeFinishedOrdersPeriod,
    [actions.CLEAR_FINISHED_ORDERS_PERIOD]: clearFinishedOrdersPeriod,
    [actions.SET_INITIAL_STATE_ORDERS]: setInitialState,
    [actions.FETCH_TAKE_ORDER_REQUEST]: fetchTakeOrderRequest,
    [actions.FETCH_TAKE_ORDER_SUCCESS]: fetchTakeOrderSuccess,
    [actions.FETCH_TAKE_ORDER_FAILURE]: fetchTakeOrderFailure,
    [actions.FETCH_TAKE_NEXT_ORDER_REQUEST]: fetchTakeNextOrderRequest,
    [actions.FETCH_TAKE_NEXT_ORDER_SUCCESS]: fetchTakeNextOrderSuccess,
    [actions.FETCH_TAKE_NEXT_ORDER_FAILURE]: fetchTakeNextOrderFailure,
  },
  initialOrdersState,
)
